import React, { FunctionComponent, useState } from 'react'
import { useTranslation } from 'next-i18next'
import {
  Button,
  ColorTypes,
  DesignLanguage,
  Divider,
  Heading,
  Icon,
  Link,
  Modal,
  Text,
  useDesignLanguagePerClass,
  useIsDesignLanguage,
} from '@afterpaytouch/core'
import styles from '../PayMonthlyOnly/style.module.css'
import { BASE_PATH } from '../../env'
import Image from 'next/image'
import { Terms } from '../PaymentTypes/PaymentTypesPBIRejected/Terms'
import { PayMonthlyOnlyPoint } from './PayMonthlyOnlyPoint/PayMonthlyOnlyPoint'
import { TrackingEvent } from '../../model/monitoring'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { PayMonthlyOnlyContinueButton } from './PayMonthlyOnlyContinueButton/PayMonthlyOnlyContinueButton'

export const PayMonthlyOnly: FunctionComponent = () => {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState<boolean>(false)
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [hasSentModalOpenAnalyticsData, setHasSentModalOpenAnalyticsData] = useState(false)
  const [hasSentModalCloseAnalyticsData, setHasSentModalCloseAnalyticsData] = useState(false)
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const headerSvgSrc = [BASE_PATH, 'images/pay-monthly-only-lightweight-background.svg'].join('/')
  const iconSvgSrc = [BASE_PATH, 'images/pay-monthly-only-lightweight-icon.svg'].join('/')

  const headerPngSrc = [BASE_PATH, 'images/pay-monthly-only-hero.png'].join('/')

  const showPbiDrawer = (): void => {
    if (!hasSentModalOpenAnalyticsData) {
      logEvent(TrackingEvent.PRESSED_PAY_MONTHLY_ONLY_LEARN_MORE_LINK)
      setHasSentModalOpenAnalyticsData(true)
    }
    setShowModal(true)
  }

  const handleClickClose = (): void => {
    if (!hasSentModalCloseAnalyticsData) {
      logEvent(TrackingEvent.CLOSED_PAY_MONTHLY_ONLY_PBI_MODAL)
      setHasSentModalCloseAnalyticsData(true)
    }
    setShowModal(false)
  }

  const pageHeaderTextColor: ColorTypes | undefined = isArcadeDesignLanguage ? 'Neutral 900' : undefined

  const renderHeaderImage = (): React.ReactNode =>
    isArcadeDesignLanguage ? (
      <Image
        data-testid='pay-monthly-only-hero-arcade'
        src={headerPngSrc}
        alt=''
        width='288'
        height='343'
        style={{
          maxWidth: '100%',
          height: 'auto',
        }}
      />
    ) : (
      <>
        <Image
          data-testid='pay-monthly-only-scene'
          src={headerSvgSrc}
          alt=''
          width='720'
          height='406'
          style={{
            maxWidth: '100%',
            height: 'auto',
          }}
        />
        <div className={styles.overlay}>
          <Image
            data-testid='pay-monthly-only-icon'
            src={iconSvgSrc}
            alt=''
            width='80'
            height='80'
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </div>
      </>
    )

  const renderHeading = (): React.ReactNode =>
    isArcadeDesignLanguage ? (
      <Text size={'XXL'} color='Neutral 900' style={{ maxWidth: '320px' }}>
        {t('consumerLending:payMonthlyOnly:title1')} {t('consumerLending:payMonthlyOnly:title2')}
      </Text>
    ) : (
      <>
        <Text bold size={'XXL'}>
          {t('consumerLending:payMonthlyOnly:title1')}
        </Text>
        <Text bold size={'XXL'}>
          {t('consumerLending:payMonthlyOnly:title2')}
        </Text>
      </>
    )

  return (
    <>
      <div>
        <div className={styles.heroImage}>{renderHeaderImage()}</div>

        <div className={designLanguageStyles.payMonthlyOnlyBottomContainer}>
          <div className={designLanguageStyles.bottomContainerPaddingTop}>
            <div>{renderHeading()}</div>
            <div className={designLanguageStyles.secondaryHeader}>
              <Text color={pageHeaderTextColor}>
                {t('consumerLending:payMonthlyOnly:subtitle')}
                <span className={styles.learnMoreButton}>
                  <Link
                    onClick={showPbiDrawer}
                    testNameSpace='learn-more'
                    kind={isArcadeDesignLanguage ? 'Underline' : 'Emphasis'}
                    color={isArcadeDesignLanguage ? 'Prominent' : undefined}
                  >
                    <Text bold color={pageHeaderTextColor}>
                      {t('consumerLending:payMonthlyOnly:learnMore')}
                    </Text>
                  </Link>
                </span>
              </Text>
            </div>

            <div className={styles.informationPoints}>
              <PayMonthlyOnlyPoint headerText={t('consumerLending:payMonthlyOnly:information:header1')} InformationIcon={Icon.Calendar} />
              <PayMonthlyOnlyPoint headerText={t('consumerLending:payMonthlyOnly:information:header2')} InformationIcon={Icon.Dollar} />
              <PayMonthlyOnlyPoint headerText={t('consumerLending:payMonthlyOnly:information:header3')} InformationIcon={Icon.PasswordHide} />
            </div>

            <Divider className={styles.termDivider} />

            <div className={styles.bottomContainer}>
              <div className={styles.buttonsContainerText}>
                <Text size='XXS' color={isArcadeDesignLanguage ? 'Neutral 500' : 'Neutral 600'} fontFace='CashSansMono'>
                  {t('consumerLending:payMonthlyOnly:term2')}
                </Text>
              </div>
              <div className={styles.termSpace}>
                <Terms />
              </div>
              <PayMonthlyOnlyContinueButton />
            </div>
          </div>
        </div>
      </div>

      <Modal
        testNameSpace='pbi-not-available'
        data-testid='pbi-not-available-modal'
        show={showModal}
        onClose={handleClickClose}
        onBackdropClick={handleClickClose}
      >
        <div className={styles.modalContainer}>
          <Modal.Header>
            <div className={styles.modalHeading}>
              <Heading size={'XL'}>{t('consumerLending:payMonthlyOnly:pbiModal:heading')}</Heading>
            </div>
          </Modal.Header>
          <Modal.Content>
            <div className={styles.modalText}>
              <Text size={'M'}>{t('consumerLending:payMonthlyOnly:pbiModal:text')}</Text>
            </div>
            <Button
              kind={isArcadeDesignLanguage ? 'Primary' : 'Secondary'}
              padding='Fluid'
              onClick={handleClickClose}
              testNameSpace='pay-monthly-only-close-modal'
            >
              {t('consumerLending:payMonthlyOnly:close')}
            </Button>
          </Modal.Content>
        </div>
      </Modal>
    </>
  )
}
